<template>
  <section class="references-section">
    <div class="container">
      <!-- Section Header -->
      <div class="section-header">
        <h2 class="section-title">{{ locale === 'tr' ? 'İş Ortaklarımız' : 'Our Business Partners' }}</h2>
        <p class="section-subtitle">{{ locale === 'tr' ? 'Güvenilir iş ortaklarımız ile birlikte büyüyoruz' : 'Growing together with our trusted business partners' }}</p>
      </div>

      <!-- References Slider -->
      <div class="references-slider" v-if="!loading && !error" @mouseenter="pauseSlider" @mouseleave="resumeSlider">
        <div class="slider-wrapper">
          <div 
            class="slider-track"
            :style="{ transform: `translateX(${translateX}px)` }"
          >
            <div 
              v-for="reference in extendedReferences" 
              :key="`${reference.id}_${reference.slideIndex}`"
              class="reference-slide"
            >
              <div class="reference-card" @click="openReference(reference)">
                <!-- Logo -->
                <div class="logo-container">
                  <img 
                    :src="getLogoUrl(reference)" 
                    :alt="reference.company_name"
                    class="reference-logo"
                    @error="handleImageError($event, reference)"
                  >
                </div>
                
                <!-- Company Name as Title -->
                <h3 class="company-title">{{ locale === 'tr' ? reference.company_name : (reference.company_name_en || reference.company_name) }}</h3>
                
                <!-- Description as Content -->
                <p class="company-content">
                  {{ locale === 'tr' ? reference.description_tr : (reference.description_en || reference.description_tr) }}
                </p>
                
                <!-- Hover Overlay -->
                <div class="card-overlay">
                  <div class="overlay-content">
                    <i class="fas fa-external-link-alt"></i>
                    <span>{{ locale === 'tr' ? 'Daha Fazlası' : 'More Info' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Dots -->
        <div class="slider-dots">
          <button 
            v-for="(dot, index) in Math.ceil(references.length / slidesPerView)"
            :key="index"
            class="dot"
            :class="{ active: index === currentSlideGroup }"
            @click="goToSlideGroup(index)"
          ></button>
        </div>
      </div>

      <!-- Featured Reference Card -->
      <div class="featured-reference" v-if="!loading && !error && centerReference">
        <div 
          class="featured-card"
          @click="openReference(centerReference)"
        >
          <div class="featured-logo-section">
            <div class="featured-logo">
              <img 
                :src="getLogoUrl(centerReference)" 
                :alt="centerReference.company_name"
                class="featured-logo-img"
                @error="handleImageError($event, centerReference)"
              >
            </div>
          </div>
          <div class="featured-content-section">
            <div class="featured-content">
              <h3 class="featured-title">
                {{ locale === 'tr' ? centerReference.company_name : (centerReference.company_name_en || centerReference.company_name) }}
              </h3>
              <p class="featured-description">
                {{ locale === 'tr' ? 
                  centerReference.description_tr : 
                  (centerReference.description_en || centerReference.description_tr) 
                }}
              </p>
              <button class="featured-btn">
                {{ locale === 'tr' ? 'Detaylı Bilgi' : 'Detailed Info' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ locale === 'tr' ? 'Referanslar yükleniyor...' : 'Loading references...' }}</p>
      </div>

      <!-- Error State -->
      <div v-if="error && !loading" class="error-state">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button @click="fetchReferences" class="retry-btn">
          {{ locale === 'tr' ? 'Tekrar Dene' : 'Try Again' }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { ApiService } from '@/services/api'

export default {
  name: 'ReferencesSection',
  
  props: {
    locale: {
      type: String,
      default: 'tr'
    }
  },
  
  data() {
    return {
      references: [],
      loading: true,
      error: null,
      logoCache: new Map(),
      
      // Slider properties
      currentSlide: 0,
      translateX: 0,
      slideWidth: 200,
      slidesPerView: 5,
      autoSlideInterval: null,
      isTransitioning: false,
      isPaused: false
    }
  },

  computed: {
    // Slider için genişletilmiş referanslar (seamless loop için)
    extendedReferences() {
      if (this.references.length === 0) return []
      
      const refs = this.references.slice(0, 30) // Maksimum 30 referans
      
      // Seamless loop için başlangıç ve bitiş kopyaları ekle
      const startCopies = refs.slice(-this.slidesPerView)
      const endCopies = refs.slice(0, this.slidesPerView)
      
      const extended = [
        ...startCopies.map((ref, index) => ({ ...ref, slideIndex: `start_${index}` })),
        ...refs.map((ref, index) => ({ ...ref, slideIndex: `main_${index}` })),
        ...endCopies.map((ref, index) => ({ ...ref, slideIndex: `end_${index}` }))
      ]
      
      return extended
    },

    // Aktif slide grubu
    currentSlideGroup() {
      return Math.floor(this.currentSlide / this.slidesPerView)
    },

    // Toplam slide sayısı (seamless loop dahil)
    totalSlides() {
      return this.references.length > 0 ? this.references.slice(0, 30).length : 0
    },

    // Ortadaki referansı hesapla
    centerReference() {
      if (this.references.length === 0) return null
      
      // Ortadaki slide'ı hesapla (5 slide görünüyor ise, ortadaki 2. index)
      const centerIndex = Math.floor(this.slidesPerView / 2)
      const actualSlide = (this.currentSlide + centerIndex) % this.references.length
      
      return this.references[actualSlide] || this.references[0]
    }
  },

  mounted() {
    this.fetchReferences().then(() => {
      this.$nextTick(() => {
        this.initializeSlider()
        this.startAutoSlide()
      })
    })
  },

  beforeDestroy() {
    this.stopAutoSlide()
    this.logoCache.clear()
  },

  methods: {
    async fetchReferences() {
      this.loading = true
      this.error = null
      
      try {
        const data = await ApiService.getReferences({ active: true, limit: 30 })
        this.references = data || this.getFallbackData()
      } catch (error) {
        console.error('References API error:', error)
        this.references = this.getFallbackData()
      } finally {
        this.loading = false
      }
    },

    // Slider Methods
    initializeSlider() {
      if (this.references.length === 0) return
      
      // İlk pozisyonu ortadaki sete ayarla (seamless loop için)
      this.currentSlide = 0
      this.translateX = -(this.slidesPerView * this.slideWidth)
    },

    startAutoSlide() {
      if (this.references.length <= this.slidesPerView) return
      
      this.stopAutoSlide()
      
      this.autoSlideInterval = setInterval(() => {
        if (!this.isPaused && !this.isTransitioning) {
          this.nextSlide()
        }
      }, 3000) // 3 saniyede bir otomatik geçiş
    },

    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval)
        this.autoSlideInterval = null
      }
    },

    nextSlide() {
      if (this.isTransitioning) return
      
      this.isTransitioning = true
      this.currentSlide++
      this.translateX -= this.slideWidth
      
      // Seamless loop: son slide'a gelince başa dön
      setTimeout(() => {
        if (this.currentSlide >= this.totalSlides) {
          this.currentSlide = 0
          this.translateX = -(this.slidesPerView * this.slideWidth)
        }
        this.isTransitioning = false
      }, 500)
    },

    prevSlide() {
      if (this.isTransitioning) return
      
      this.isTransitioning = true
      this.currentSlide--
      this.translateX += this.slideWidth
      
      // Seamless loop: ilk slide'a gelince sona git
      setTimeout(() => {
        if (this.currentSlide < 0) {
          this.currentSlide = this.totalSlides - 1
          this.translateX = -(this.slidesPerView + this.totalSlides - 1) * this.slideWidth
        }
        this.isTransitioning = false
      }, 500)
    },

    goToSlideGroup(groupIndex) {
      if (this.isTransitioning) return
      
      const targetSlide = groupIndex * this.slidesPerView
      this.currentSlide = targetSlide
      this.translateX = -(this.slidesPerView + targetSlide) * this.slideWidth
    },

    pauseSlider() {
      this.isPaused = true
    },

    resumeSlider() {
      this.isPaused = false
    },

    getFallbackData() {
      return [
        {
          id: "1",
          company_name: "Türkiye Elektrik İletim A.Ş. (TEİAŞ)",
          company_name_en: "Turkish Electricity Transmission Corporation",
          logo_url: "https://www.petroturk.com/wp-content/uploads/2023/06/yeo-enerji-yatirimlari-as.jpg",
          website_url: "https://www.teias.gov.tr/",
          description_tr: "Türkiye'nin elektrik iletim şebekesinin sahibi ve işletmecisi",
          description_en: "Owner and operator of Turkey's electricity transmission network",
          sort_order: "1",
          is_active: "1",
          created_at: "2025-10-15 03:00:27",
          updated_at: "2025-10-15 23:45:41"
        },
        {
          id: "2",
          company_name: "Rey Energy",
          company_name_en: "Rey Energy Solutions",
          logo_url: "text-logo",
          website_url: "https://www.reyenergy.com/",
          description_tr: "Yenilenebilir enerji çözümleri ve sürdürülebilir teknolojiler.",
          description_en: "Renewable energy solutions and sustainable technologies.",
          sort_order: "2",
          is_active: "1"
        },
        {
          id: "3",
          company_name: "Mikrohes",
          company_name_en: "Microhydro Systems",
          logo_url: "text-logo",
          website_url: "https://www.mikrohes.com/",
          description_tr: "Mikro hidroelektrik santralleri ve enerji üretim sistemleri.",
          description_en: "Micro hydroelectric power plants and energy generation systems.",
          sort_order: "3",
          is_active: "1"
        },
        {
          id: 4,
          company_name: 'DEPC Globe',
          logo_url: 'text-logo',
          website_url: 'https://www.depcglobe.com/',
          description_tr: 'Güç elektroniği ve enerji dönüşüm sistemleri.',
          description_en: 'Power electronics and energy conversion systems.',
          sort_order: 4,
          is_active: 1
        },
        {
          id: 5,
          company_name: 'LTG Technologies',
          logo_url: 'text-logo',
          website_url: 'https://www.ltgtech.com/',
          description_tr: 'Endüstriyel teknolojiler ve otomasyon sistemleri.',
          description_en: 'Industrial technologies and automation systems.',
          sort_order: 5,
          is_active: 1
        },
        {
          id: 6,
          company_name: 'hydrogen',
          logo_url: 'text-logo',
          website_url: 'https://www.hydrogen.com/',
          description_tr: 'Hidrojen teknolojileri ve temiz enerji çözümleri.',
          description_en: 'Hydrogen technologies and clean energy solutions.',
          sort_order: 6,
          is_active: 1
        },
        {
          id: 7,
          company_name: 'marinergy',
          logo_url: 'text-logo',
          website_url: 'https://www.marinergy.com/',
          description_tr: 'Deniz enerji sistemleri ve dalga enerjisi teknolojileri.',
          description_en: 'Marine energy systems and wave energy technologies.',
          sort_order: 7,
          is_active: 1
        },
        {
          id: 8,
          company_name: 'ni-cat',
          logo_url: 'text-logo',
          website_url: 'https://www.nicat.com/',
          description_tr: 'Katalitik sistemler ve çevre dostu teknolojiler.',
          description_en: 'Catalytic systems and environmentally friendly technologies.',
          sort_order: 8,
          is_active: 1
        },
        {
          id: 9,
          company_name: 'ION Technologies',
          logo_url: 'text-logo',
          website_url: 'https://www.iontech.com/',
          description_tr: 'İyon teknolojileri ve elektrokimyasal sistemler.',
          description_en: 'Ion technologies and electrochemical systems.',
          sort_order: 9,
          is_active: 1
        },
        {
          id: 10,
          company_name: 'Schneider Electric',
          logo_url: 'text-logo',
          website_url: 'https://www.se.com/',
          description_tr: 'Enerji yönetimi ve otomasyon çözümleri.',
          description_en: 'Energy management and automation solutions.',
          sort_order: 10,
          is_active: 1
        }
      ]
    },



    openReference(reference) {
      if (reference.website_url) {
        window.open(reference.website_url, '_blank', 'noopener,noreferrer')
      }
    },

    getLogoUrl(reference) {
      // Debug için logo_url'i console'a yazdır
      // console.log('Reference logo_url:', reference.logo_url)
      // console.log('Company name:', reference.company_name)
      
      // Eğer logo_url var ve geçerli bir URL ise direkt döndür
      if (reference.logo_url && 
          reference.logo_url !== 'text-logo' && 
          reference.logo_url !== '' && 
          reference.logo_url !== null &&
          reference.logo_url.startsWith('http')) {
        
        // console.log('Using database logo URL:', reference.logo_url)
        return reference.logo_url
      }
      
      // Fallback text logo
      // console.log('Using text logo for:', reference.company_name)
      return this.generateTextLogo(reference.company_name || 'Logo')
    },



    generateTextLogo(companyName) {
      try {
        // Şirket adının baş harflerini al
        if (!companyName || typeof companyName !== 'string') {
          companyName = 'LOGO'
        }
        
        const initials = companyName
          .split(' ')
          .filter(word => word && word.length > 0)
          .map(word => word.charAt(0).toUpperCase())
          .join('')
          .substring(0, 2) || 'LG'
        
        // Renk paleti
        const colors = [
          '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'
        ]
        
        const colorIndex = Math.abs(companyName.charCodeAt(0) || 0) % colors.length
        const mainColor = colors[colorIndex]
        const gradientColor = colors[(colorIndex + 1) % colors.length]
        
        // Temiz SVG - direkt string olarak
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="140" height="60" viewBox="0 0 140 60">
<defs>
<linearGradient id="grad${Date.now()}" x1="0%" y1="0%" x2="100%" y2="100%">
<stop offset="0%" style="stop-color:${mainColor}"/>
<stop offset="100%" style="stop-color:${gradientColor}"/>
</linearGradient>
</defs>
<rect width="140" height="60" rx="12" fill="url(#grad${Date.now()})" stroke="${mainColor}" stroke-width="2"/>
<text x="70" y="38" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="18" font-weight="bold">${initials}</text>
</svg>`
        
        // Proper encoding
        return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
        
      } catch (error) {
        // console.error('Logo generation error:', error)
        // Basit fallback
        return 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="140" height="60"><rect width="140" height="60" fill="#ddd"/><text x="70" y="35" text-anchor="middle" fill="white" font-size="16">LOGO</text></svg>')
      }
    },

    handleImageError(event, reference) {
      // Hata durumunda text logo
      event.target.onerror = null
      const companyName = reference.company_name || reference.company_name_en || 'Logo'
      event.target.src = this.generateTextLogo(companyName)
    }
  }
}
</script>

<style scoped>
.references-section {
  padding: 60px 0;
  background: #ffffff;
  position: relative;
  overflow: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Section Header */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #2c3e50;
  margin: 0 0 1rem 0;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  font-weight: 400;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
}

/* References Slider */
.references-slider {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.slider-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.slider-track {
  display: flex;
  transition: transform 0.5s ease-in-out;
  will-change: transform;
}

.reference-slide {
  flex: 0 0 200px;
  margin-right: 0;
  position: relative;
}

/* Reference Card - Sabit Yapı */
.reference-card {
  width: 180px;
  height: 280px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 20px;
  margin: 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.reference-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #4CAF50;
}

/* Center Card Highlight */
.reference-slide:nth-child(3) .reference-card {
  border: 2px solid #FFD700;
  background: linear-gradient(145deg, #ffffff 0%, #fffef8 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.reference-slide:nth-child(3) .reference-card:hover {
  transform: scale(1.05) translateY(-5px);
  border-color: #FFA500;
  box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
}

/* Logo Container */
.logo-container {
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: #f9f9f9;
  border-radius: 6px;
  padding: 10px;
}

.reference-logo {
  max-width: 100%;
  max-height: 60px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.reference-card:hover .reference-logo {
  transform: scale(1.05);
}

/* Company Title */
.company-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
  line-height: 1.3;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Company Content */
.company-content {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Card Overlay */
.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(255, 165, 0, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  border-radius: 8px;
  backdrop-filter: blur(5px);
}

.reference-card:hover .card-overlay {
  opacity: 1;
  visibility: visible;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
}

.overlay-content i {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}

/* Slider Navigation Dots */
.slider-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: #ddd;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: #4CAF50;
  transform: scale(1.2);
}

.dot:hover {
  background: #4CAF50;
  transform: scale(1.1);
}

/* Loading & Error States */
.loading-state,
.error-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state i {
  font-size: 2.5rem;
  color: #f44336;
  margin-bottom: 1rem;
}

.retry-btn {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 1rem;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: #45a049;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .references-section {
    padding: 40px 0;
  }
  
  .container {
    padding: 0 1rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .reference-slide {
    flex: 0 0 180px;
  }
  
  .reference-card {
    width: 160px;
    height: 260px;
    padding: 15px;
    margin: 0 5px;
  }
  
  .company-title {
    font-size: 1rem;
    min-height: 40px;
  }
  
  .company-content {
    font-size: 0.8rem;
  }
  
  .overlay-content {
    font-size: 0.8rem;
  }
  
  .overlay-content i {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 1.8rem;
  }
  
  .reference-slide {
    flex: 0 0 160px;
  }
  
  .reference-card {
    width: 140px;
    height: 240px;
    padding: 12px;
  }
  
  .logo-container {
    height: 60px;
  }
  
  .reference-logo {
    max-height: 45px;
  }
  
  .company-title {
    font-size: 0.9rem;
    min-height: 36px;
  }
  
  .company-content {
    font-size: 0.75rem;
  }
}

/* Slider pause on hover */
.references-slider:hover .slider-track {
  animation-play-state: paused;
}

/* Featured Reference Card */
.featured-reference {
  margin-top: 3rem;
  padding: 0 2rem;
}

.featured-card {
  background: #ffffff;
  border-radius: 16px;
  display: flex;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
}

.featured-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.featured-logo-section {
  flex: 0 0 350px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
}

.featured-logo {
  width: 200px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.featured-logo-img {
  max-width: 100%;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.featured-card:hover .featured-logo-img {
  transform: scale(1.05);
}

.featured-content-section {
  flex: 1;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  position: relative;
  clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%);
  padding-left: 4rem;
}

.featured-content {
  padding: 2rem 2rem 2rem 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
}

.featured-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  margin: 0 0 1rem 0;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.featured-description {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.featured-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  backdrop-filter: blur(10px);
}

.featured-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

/* Featured Card Responsive */
@media (max-width: 768px) {
  .featured-reference {
    margin-top: 2rem;
    padding: 0 1rem;
  }
  
  .featured-card {
    flex-direction: column;
    max-width: 100%;
  }
  
  .featured-logo-section {
    flex: none;
    width: 100%;
    padding: 1.5rem;
  }
  
  .featured-logo {
    width: 150px;
    height: 80px;
  }
  
  .featured-logo-img {
    max-height: 60px;
  }
  
  .featured-content-section {
    clip-path: none;
    padding-left: 0;
  }
  
  .featured-content {
    padding: 1.5rem;
    text-align: center;
  }
  
  .featured-title {
    font-size: 1.4rem;
  }
  
  .featured-description {
    font-size: 0.9rem;
  }
  
  .featured-btn {
    align-self: center;
  }
}

@media (max-width: 480px) {
  .featured-logo-section {
    padding: 1rem;
  }
  
  .featured-logo {
    width: 120px;
    height: 70px;
  }
  
  .featured-content {
    padding: 1rem;
  }
  
  .featured-title {
    font-size: 1.2rem;
  }
  
  .featured-description {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
  
  .featured-btn {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
  }
}
</style>